import { Button, Drawer, Flex, MultiSelect, Select, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { UserRowWithActions } from '@dealstack/frontend/shared/components/UserRowWithActions/UserRowWithActions.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMipClassifiersList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers.ts';
import {
	attachInvestorsToClassifier,
	detachInvestorsFromClassifier,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/services/mipClassifiers.service.ts';
import { ClassifierType } from '@dealstack/shared/lib/generated';
import { BULK_COHORTS_MODE, BulkCohortsMode } from './types';

interface Props {
	mipId: string;
	investorIds: string[];
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const BulkManageInvestorCohortsDrawer = ({
	mipId,
	investorIds,
	opened,
	onClose,
	onSuccess,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [mode, setMode] = useState<BulkCohortsMode>(BULK_COHORTS_MODE.add);
	const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);

	useEffect(() => {
		if (!opened) {
			setMode(BULK_COHORTS_MODE.add);
			setSelectedCohortIds([]);
		}
	}, [opened]);

	const { data: cohorts = [], isLoading: cohortsLoading } = useMipClassifiersList({
		mipId,
		isArchived: false,
		classifiedType: ClassifierType.INVESTOR_COHORT,
		enabled: opened,
	});

	const cohortOptions = useMemo(
		() => cohorts.map((c) => ({ value: c.id, label: c.name })),
		[cohorts]
	);

	const selectedCohorts = useMemo(
		() => cohorts.filter((c) => selectedCohortIds.includes(c.id)),
		[cohorts, selectedCohortIds]
	);

	const removeFromSelection = (cohortId: string) => {
		setSelectedCohortIds((prev) => prev.filter((id) => id !== cohortId));
	};

	const attachMutation = useMutation({
		mutationFn: attachInvestorsToClassifier,
	});
	const detachMutation = useMutation({
		mutationFn: detachInvestorsFromClassifier,
	});
	const isSubmitting = attachMutation.isPending || detachMutation.isPending;

	const noInvestorsSelected = investorIds.length === 0;
	const noCohortsAvailable = cohortOptions.length === 0 && !cohortsLoading;
	const canSubmit = !noInvestorsSelected && selectedCohortIds.length > 0 && !isSubmitting;

	const onSubmit = async () => {
		if (!canSubmit) {
			return;
		}
		const mutate =
			mode === BULK_COHORTS_MODE.add
				? attachMutation.mutateAsync
				: detachMutation.mutateAsync;
		try {
			await Promise.all(
				selectedCohortIds.map((mipClassifierId) =>
					mutate({ mipClassifierId, body: { investorIds } })
				)
			);
			showToastV2({
				state: 'success',
				title:
					mode === BULK_COHORTS_MODE.add
						? tMipManage('investors.bulk_cohorts_drawer.toast.add_success')
						: tMipManage('investors.bulk_cohorts_drawer.toast.remove_success'),
			});
			onClose();
			onSuccess?.();
		} catch {
			showToastV2({
				state: 'error',
				title: tMipManage('investors.bulk_cohorts_drawer.toast.error'),
			});
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investors.bulk_cohorts_drawer.title')}
		>
			<Flex direction="column" justify="space-between" h="100%" gap="1.5rem">
				<Flex direction="column" gap="1rem">
					<Select
						value={mode}
						onChange={(v: BulkCohortsMode) => setMode(v)}
						data={[
							{
								value: BULK_COHORTS_MODE.add,
								label: tMipManage('investors.bulk_cohorts_drawer.mode.add'),
							},
							{
								value: BULK_COHORTS_MODE.remove,
								label: tMipManage('investors.bulk_cohorts_drawer.mode.remove'),
							},
						]}
						label={tMipManage('investors.bulk_cohorts_drawer.mode_label')}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						size="md"
						allowDeselect={false}
					/>

					<MultiSelect
						data={cohortOptions}
						value={selectedCohortIds}
						onChange={setSelectedCohortIds}
						label={tMipManage('investors.bulk_cohorts_drawer.cohort_label')}
						placeholder={tMipManage('investors.bulk_cohorts_drawer.cohort_placeholder')}
						disabled={cohortsLoading || noCohortsAvailable}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						size="md"
						clearable
						searchable
					/>

					{noCohortsAvailable && (
						<InfoBox
							iconVariant="information"
							alignContent="center"
							description={tMipManage(
								'investors.bulk_cohorts_drawer.no_cohorts_warning'
							)}
						/>
					)}

					{selectedCohorts.length > 0 && (
						<Flex direction="column">
							<Text fw={600}>
								{tMipManage('investors.bulk_cohorts_drawer.selected_title')}
							</Text>
							<Text c="gray.9" mt="0.25rem" mb="0.5rem">
								{mode === BULK_COHORTS_MODE.add
									? tMipManage(
											'investors.bulk_cohorts_drawer.selected_description_add',
											{ count: investorIds.length }
										)
									: tMipManage(
											'investors.bulk_cohorts_drawer.selected_description_remove',
											{ count: investorIds.length }
										)}
							</Text>
							{selectedCohorts.map((c) => (
								<UserRowWithActions
									key={c.id}
									displayName={c.name}
									onUserDeleteClick={() => removeFromSelection(c.id)}
									hasEditPermission={true}
									showInitialsAvatar={false}
								/>
							))}
						</Flex>
					)}
				</Flex>

				<Button onClick={onSubmit} disabled={!canSubmit}>
					{mode === BULK_COHORTS_MODE.add
						? tMipManage('investors.bulk_cohorts_drawer.submit_add')
						: tMipManage('investors.bulk_cohorts_drawer.submit_remove')}
				</Button>
			</Flex>
		</Drawer>
	);
};
